import React, { useState } from 'react';
import { MdOutlineFileCopy } from "react-icons/md";


const CallEventList = ({ data, filters }) => {
  // Parse Date + Time properly for each item (overwrite Date with Date object)
  const normalizedData = data; // already normalized upstream

  const [segmentFilter, setSegmentFilter] = useState('');

  // Filter by customer, agent, and selectedDate (compare date strings YYYY-MM-DD)
  const filtered = normalizedData.filter((d) => {
    const matchCustomer = !filters.customer || d.Customer === filters.customer;
    const matchAgent = !filters.agent || d.Agent === filters.agent;
    const matchDate = !filters.selectedDate || d.ParsedDate.toLocaleDateString('en-CA') === filters.selectedDate;
    const matchSegment = !segmentFilter || (d.Segment && d.Segment.toLowerCase().includes(segmentFilter.toLowerCase()));

    return matchCustomer && matchAgent && matchDate && matchSegment;
  });

  const [visibleCount, setVisibleCount] = useState(15);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  

  // Checkbox filters for types, Handled off by default
  const [typeFilters, setTypeFilters] = useState({
    Handled: false,
    Abandoned: true,
    Missed: true,
    Rejected: true,
  });


  const toggleTypeFilter = (type) => {
    setTypeFilters((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  const determineAbandonReason = (segmentMap, entry) => {
    const segment = segmentMap[entry.Segment];
    if (!segment || !entry.Abandoned) return '';

    if (segment.hadMissedOrRejected) return 'Abandoned - Na gemiste/geweigerde oproep';
    if (segment.hadRing) return 'Abandoned - Gedurende beltoon';
    return 'Abandoned - Geen interactie';
  };

  // Build segment context
  const segmentMap = {};
  filtered.forEach((d) => {
    const id = d.Segment;
    if (!segmentMap[id]) {
      segmentMap[id] = {
        hadMissedOrRejected: false,
        hadRing: false,
      };
    }

    if ((d.Missed === 1 && d.RingTime >= 20) || d.Rejected === 1) {
      segmentMap[id].hadMissedOrRejected = true;
    }

    if (d.RingTime > 0) {
      segmentMap[id].hadRing = true;
    }
  });

  // Normalize all events into a flat list filtered by typeFilters
  const allEvents = [];

  filtered.forEach((d) => {
    const base = {
      Segment: d.Segment,
      Agent: d.Agent || '—',
      Customer: d.Customer,
      RawDate: d.RawDate,
      RawTime: d.RawTime,
    };


    if (d.Handled === 1 && typeFilters.Handled) {
      allEvents.push({ ...base, Type: 'Handled', Detail: '' });
    }
    if (d.Rejected === 1 && typeFilters.Rejected) {
      allEvents.push({ ...base, Type: 'Rejected', Detail: '' });
    }
    if (d.Missed === 1 && d.RingTime >= 20 && typeFilters.Missed) {
      allEvents.push({ ...base, Type: 'Missed', Detail: '' });
    }
    if (d.Abandoned === 1 && typeFilters.Abandoned) {
      const reason = determineAbandonReason(segmentMap, d);
      allEvents.push({ ...base, Type: 'Abandoned', Detail: reason });
    }
  });

  const handleShowMore = () => setVisibleCount((prev) => prev + itemsPerPage);
  const handleItemsPerPageChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setItemsPerPage(value);
    setVisibleCount(value);
  };

  // New state for bottom-left message visibility
  const [copyMessageVisible, setCopyMessageVisible] = useState(false);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopyMessageVisible(true);
        setTimeout(() => setCopyMessageVisible(false), 2500);
      })
      .catch(() => {
        // Optional: handle copy failure
      });
  };


  return (
    <div style={{ padding: '20px', width: 'calc(100% - 5rem)', margin: '0 auto', backgroundColor: 'white', boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)' }}>
      <div style={{
        backgroundColor: 'white',
        padding: '1rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: 'solid 0.2rem #e6f2f6'
      }}>
        <h3 style={{ marginBottom: '1rem' }}>📄 All Call Events</h3>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <label style={{ fontSize: '1rem', display: 'inline-block', marginRight: '1rem' }}>
            <strong style={{ fontSize: '0.95rem', color: '#555', marginRight: '0.5rem' }}>Segment ID</strong>
            <input
              type="text"
              value={segmentFilter}
              onChange={(e) => setSegmentFilter(e.target.value)}
              placeholder="Search by Segment"
              style={{
                padding: '0.2rem 0.4rem',
                fontSize: '0.9rem',
                borderRadius: '4px',
                border: '1px solid #ccc',
                minWidth: '180px'
              }}
            />
          </label>

          <label style={{ fontSize: '1rem', display: 'inline-block', marginRight: '1.5rem' }}>
            <strong style={{ fontSize: '0.95rem', color: '#555', marginRight: '0.5rem' }}>Filters:</strong>
            <label style={{ marginRight: '0.8rem' }}>
              <input
                type="checkbox"
                checked={typeFilters.Handled}
                onChange={() => toggleTypeFilter('Handled')}
              /> Handled
            </label>
            <label style={{ marginRight: '0.8rem' }}>
              <input
                type="checkbox"
                checked={typeFilters.Abandoned}
                onChange={() => toggleTypeFilter('Abandoned')}
              /> Abandoned
            </label>
            <label style={{ marginRight: '0.8rem' }}>
              <input
                type="checkbox"
                checked={typeFilters.Missed}
                onChange={() => toggleTypeFilter('Missed')}
              /> Missed
            </label>
            <label>
              <input
                type="checkbox"
                checked={typeFilters.Rejected}
                onChange={() => toggleTypeFilter('Rejected')}
              /> Rejected
            </label>
          </label>
          
          <label style={{ fontSize: '1rem', display: 'inline-block' }}>
            <strong style={{ fontSize: '0.95rem', color: '#555', marginRight: '1rem' }}>Items per page</strong>
            <select value={itemsPerPage} onChange={handleItemsPerPageChange} style={{ marginRight: '1rem' }}>
              <option value={15}>15</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
          <div style={{ fontSize: '0.95rem', color: '#555' }}>
            <strong>Items:</strong> {allEvents.length} &nbsp;
          </div>
        </div>
      </div>

      <ul style={{ listStyle: 'none', padding: 0, marginTop: 10 }}>
        {allEvents.slice(0, visibleCount).map((item, index) => (
          <li key={`${item.Segment}-${item.Type}-${index}`} style={{
            padding: '0.5rem',
            borderBottom: '1px solid #eee',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            fontSize: '0.9rem'
          }}>
            <div style={{ textAlign: 'left', width: '20%', borderRight: 'solid 0.2rem lightgray', marginRight: '2rem' }}>
              <div>{item.Agent}</div>
              <div style={{ fontSize: '0.85rem', color: '#999' }}>{item.Customer}</div>
              <p>{item.RawDate} {item.RawTime}</p>
            </div>
            <div>
              <strong>{item.Type}</strong> &nbsp;
              <span style={{ color: '#555' }}>{item.Detail && `(${item.Detail})`}</span>
                <div style={{ fontSize: '0.85rem', color: '#777', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem' }}>
                  <p>{item.Segment}</p>
                  <section
                    onClick={() => copyToClipboard(item.Segment)}
                    style={{ cursor: 'pointer', padding: '2px 6px', fontSize: '1.3rem' }}
                    aria-label={`Copy segment ${item.Segment}`}
                  >
                    <MdOutlineFileCopy />
                  </section>
                </div>
            </div>
          </li>
        ))}
      </ul>

      {visibleCount < allEvents.length && (
        <button onClick={handleShowMore} style={{ marginTop: 15 }}>
          Load 15 more
        </button>
      )}

      {/* Copy success message fixed bottom-left */}
      {copyMessageVisible && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '20px',
          backgroundColor: '#4caf50',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '4px',
          fontSize: '0.9rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          zIndex: 9999,
          userSelect: 'none'
        }}>
          Successvol gekopieerd!
        </div>
      )}
    </div>
  );
};

export default CallEventList;